import { schools, schoolOfAbility } from '../../content/spells/schools.js';
import { bakeCursors, CURSOR_SIZE } from '../rendering/cursors/cursor-forge.js';

/**
 * The game's own cursors. They are baked once at start into image URLs and exposed as CSS custom properties (`--cursor-default`,
 * `--cursor-move`…), which the stylesheets use with the browser's cursor as fallback until the bake finishes. `docs/controls.md`
 * lists the states.
 */

interface Installed { url: string; large: string; hotspot: [number, number] }
const installed = new Map<string, Installed>();
let baking: Promise<void> | null = null;
/** The browser cursor each one stands in for, if its image fails to load. */
const KEYWORDS: Record<string, string> = { default: 'default', press: 'pointer', move: 'pointer', attack: 'pointer', talk: 'pointer', pickup: 'pointer', cast: 'crosshair', invalid: 'not-allowed', grab: 'grabbing', aim: 'crosshair' };

const blobUrl = (canvas: HTMLCanvasElement) => new Promise<string>((resolve, reject) =>
  canvas.toBlob(blob => blob ? resolve(URL.createObjectURL(blob)) : reject(new Error('The cursor did not encode.')), 'image/png'));

/** Bakes the cursors and defines their custom properties on the page. Safe to call more than once. */
export function installCursors(): Promise<void> {
  baking ??= (async () => {
    const gems = Object.fromEntries(schools.map(school => [school.id, school.palette.glow]));
    const rules: string[] = [];
    for (const cursor of bakeCursors(gems)) {
      const [url, large] = await Promise.all(cursor.images.map(blobUrl));
      const [x, y] = cursor.hotspot;
      installed.set(cursor.name, { url, large, hotspot: cursor.hotspot });
      rules.push(`--cursor-${cursor.name}: image-set(url("${url}") 1x, url("${large}") 2x) ${x} ${y}, ${KEYWORDS[cursor.name.replace(/-.*/, '')] ?? 'auto'};`);
    }
    if (!rules.length) return;
    const style = document.createElement('style'); style.dataset.cursors = '';
    style.textContent = `:root { ${rules.join(' ')} }`;
    document.head.append(style);
  })().catch(error => console.warn('Custom cursors are unavailable.', error));
  return baking;
}

/** The cast reticle takes the armed power's school colour; null disarms. */
export function armCursor(abilityId: string | null) {
  const school = abilityId ? schoolOfAbility(abilityId) : undefined;
  if (school) document.documentElement.style.setProperty('--cursor-cast-armed', `var(--cursor-cast-${school.id})`);
  else document.documentElement.style.removeProperty('--cursor-cast-armed');
}

/**
 * The image to draw for a computed `cursor` value, for the pointer-lock cursor (which the browser does not draw): the baked cursor it
 * names, or the plain pointer for anything else.
 */
export function cursorImage(computed: string): Installed | undefined {
  for (const cursor of installed.values()) if (computed.includes(cursor.url)) return cursor;
  return installed.get('default');
}

export { CURSOR_SIZE };
